import { useState } from "react";
import { useLocation, Link } from "wouter";
import { useListProjects, type ProjectListItem } from "@workspace/api-client-react";
import { usePageTitle } from "@/hooks/use-page-title";
import { useAuth, getTrialInfo } from "@/hooks/use-auth";
import { useFieldMode } from "@/hooks/use-field-mode";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { HardHat, Search, MapPin, Camera, Navigation2, Loader2, Smartphone, Wrench, ChevronRight, Lock, Sparkles, Clock, Image } from "lucide-react";
import { Button } from "@/components/ui/button";

function haversineDistanceMiles(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 3958.8;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.asin(Math.sqrt(a));
}

function formatDist(miles: number): string {
  if (miles < 0.1) return "< 0.1 mi";
  if (miles < 10) return `${miles.toFixed(1)} mi`;
  return `${Math.round(miles)} mi`;
}

const STATUS_COLORS: Record<string, string> = {
  lead: "#94a3b8",
  quoted: "#3b82f6",
  approved: "#8b5cf6",
  in_progress: "#f59e0b",
  complete: "#10b981",
  archived: "#6b7280",
};

const STATUS_LABELS: Record<string, string> = {
  lead: "Lead",
  quoted: "Quoted",
  approved: "Approved",
  in_progress: "In Progress",
  complete: "Complete",
  archived: "Archived",
};

const SAMPLE_PROJECTS = [
  { name: "Riverside Kitchen Reno", status: "in_progress", address: "142 Riverside Dr", dist: "0.4 mi", photos: 12 },
  { name: "Oakwood Deck Build", status: "approved", address: "87 Oakwood Blvd", dist: "2.1 mi", photos: 4 },
  { name: "Sunnybrook Bath Remodel", status: "in_progress", address: "310 Sunnybrook Ln", dist: "5.8 mi", photos: 7 },
];

const SAMPLE_STATUS_COLORS: Record<string, string> = {
  in_progress: "#f59e0b",
  approved: "#8b5cf6",
};

const SAMPLE_STATUS_LABELS: Record<string, string> = {
  in_progress: "In Progress",
  approved: "Approved",
};

function FieldProTeaser() {
  return (
    <div>
      {/* Header — same as pro */}
      <div style={{ marginBottom: "1.25rem" }}>
        <h1 style={{ fontSize: "1.875rem", fontWeight: 700, marginBottom: "0.25rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <HardHat style={{ width: "1.75rem", height: "1.75rem", color: "hsl(var(--primary))" }} />
          Field
        </h1>
        <p style={{ color: "hsl(var(--muted-foreground))", fontSize: "0.875rem" }}>
          Jobsite tools and active project tracking.
        </p>
      </div>

      {/* Field Tools banner — free for everyone */}
      <Link href="/field/tools">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.875rem",
            padding: "0.875rem 1rem",
            marginBottom: "1.5rem",
            backgroundColor: "hsl(var(--primary))",
            borderRadius: "0.5rem",
            cursor: "pointer",
            color: "hsl(var(--primary-foreground))",
          }}
          className="hover:opacity-90 transition-opacity"
        >
          <Wrench style={{ width: "1.25rem", height: "1.25rem", flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: "0.9375rem", lineHeight: 1.3 }}>Field Tools</div>
            <div style={{ fontSize: "0.8rem", opacity: 0.85, marginTop: "0.1rem" }}>Notes · calculators · wall framer · stairs · references</div>
          </div>
          <ChevronRight style={{ width: "1.125rem", height: "1.125rem", opacity: 0.7, flexShrink: 0 }} />
        </div>
      </Link>

      {/* Locked project tracking section */}
      <div className="relative">
        {/* Feature highlight pills */}
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "1rem" }}>
          {[
            { icon: MapPin, label: "GPS-sorted by distance" },
            { icon: Camera, label: "Jobsite photo log" },
            { icon: Clock, label: "Labor time tracking" },
            { icon: Image, label: "Per-project photo albums" },
          ].map(({ icon: Icon, label }) => (
            <span
              key={label}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.375rem",
                padding: "0.25rem 0.625rem",
                borderRadius: "9999px",
                fontSize: "0.75rem",
                fontWeight: 500,
                backgroundColor: "hsl(var(--muted))",
                color: "hsl(var(--muted-foreground))",
                opacity: 0.6,
              }}
            >
              <Icon style={{ width: "0.75rem", height: "0.75rem" }} />
              {label}
            </span>
          ))}
        </div>

        {/* Blurred sample project cards */}
        <div style={{ display: "grid", gap: "0.75rem", filter: "blur(2px)", pointerEvents: "none", userSelect: "none", opacity: 0.5 }}>
          {SAMPLE_PROJECTS.map((p) => (
            <Card key={p.name}>
              <CardContent style={{ padding: "1rem" }}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "0.75rem" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                      <span style={{ fontWeight: 600, fontSize: "1rem" }}>{p.name}</span>
                      <Badge
                        style={{
                          backgroundColor: SAMPLE_STATUS_COLORS[p.status] + "22",
                          color: SAMPLE_STATUS_COLORS[p.status],
                          border: `1px solid ${SAMPLE_STATUS_COLORS[p.status]}44`,
                          fontSize: "0.7rem",
                          padding: "0 0.4rem",
                        }}
                      >
                        {SAMPLE_STATUS_LABELS[p.status]}
                      </Badge>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.25rem", marginTop: "0.25rem", fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))" }}>
                      <MapPin style={{ width: "0.75rem", height: "0.75rem", flexShrink: 0 }} />
                      {p.address}
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexShrink: 0 }}>
                    <span style={{ fontSize: "0.75rem", fontWeight: 600, padding: "0.125rem 0.5rem", borderRadius: "9999px", backgroundColor: "hsl(var(--muted))", color: "hsl(var(--muted-foreground))" }}>
                      {p.dist}
                    </span>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.25rem", fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))" }}>
                      <Camera style={{ width: "0.875rem", height: "0.875rem" }} />
                      <span>{p.photos}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Upgrade overlay */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.75rem",
            borderRadius: "0.5rem",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "0.625rem",
              padding: "1.5rem 2rem",
              borderRadius: "0.75rem",
              backgroundColor: "hsl(var(--background))",
              border: "1px solid hsl(var(--border))",
              boxShadow: "0 4px 24px 0 rgba(0,0,0,0.10)",
              textAlign: "center",
              maxWidth: "320px",
            }}
          >
            <div style={{ width: "2.5rem", height: "2.5rem", borderRadius: "9999px", backgroundColor: "hsl(var(--primary) / 0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Lock style={{ width: "1.25rem", height: "1.25rem", color: "hsl(var(--primary))" }} />
            </div>
            <div>
              <p style={{ fontWeight: 700, fontSize: "1rem", marginBottom: "0.25rem" }}>Field Project Tracking</p>
              <p style={{ fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))", lineHeight: 1.5 }}>
                Track active jobs on-site — jobsite photos, labor time, and GPS-sorted project list.
              </p>
            </div>
            <Link href="/upgrade">
              <Button size="sm" className="gap-1.5 mt-1">
                <Sparkles style={{ width: "0.875rem", height: "0.875rem" }} />
                Upgrade to Pro
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function FieldIndexPage() {
  usePageTitle("Field");
  const { user } = useAuth();
  const { hasProAccess } = getTrialInfo(user);
  const { fieldMode, setFieldMode } = useFieldMode();
  const isOwner = user?.role === "owner";

  if (!hasProAccess) {
    return <FieldProTeaser />;
  }

  return (
    <>
      {isOwner && !fieldMode && (
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "0.75rem",
          padding: "0.75rem 1rem",
          marginBottom: "1.25rem",
          backgroundColor: "hsl(var(--muted))",
          border: "1px solid hsl(var(--border))",
          borderRadius: "0.5rem",
          flexWrap: "wrap",
        }}>
          <Smartphone style={{ width: "1.125rem", height: "1.125rem", color: "hsl(var(--muted-foreground))", flexShrink: 0 }} />
          <span style={{ fontSize: "0.875rem", color: "hsl(var(--muted-foreground))", flex: 1 }}>
            Using the field app regularly? Switch to a distraction-free mobile view.
          </span>
          <Button size="sm" onClick={() => setFieldMode(true)}>
            Enter field worker view
          </Button>
        </div>
      )}
      <FieldIndexContent />
    </>
  );
}

function FieldIndexContent() {
  const [, setLocation] = useLocation();
  const [search, setSearch] = useState("");
  const [sortByNearest, setSortByNearest] = useState(false);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [isLocating, setIsLocating] = useState(false);

  const projectsQuery = useListProjects({
    status: "in_progress",
  });

  // Also fetch all statuses for field workers to see all active jobs
  const allProjectsQuery = useListProjects();
  const projects = (allProjectsQuery.data ?? []).filter(
    (p: ProjectListItem) => p.status !== "archived" && p.status !== "complete",
  );

  const handleNearbyToggle = () => {
    if (!userLocation && !isLocating) {
      setIsLocating(true);
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
            setSortByNearest(true);
            setIsLocating(false);
          },
          () => setIsLocating(false),
        );
      } else {
        setIsLocating(false);
      }
    } else {
      setSortByNearest((v) => !v);
    }
  };

  const filtered = projects
    .filter((p: ProjectListItem) => !search || p.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a: ProjectListItem, b: ProjectListItem) => {
      if (sortByNearest && userLocation) {
        const aLat = (a as any).lat, aLng = (a as any).lng;
        const bLat = (b as any).lat, bLng = (b as any).lng;
        const dA = aLat != null && aLng != null ? haversineDistanceMiles(userLocation.lat, userLocation.lng, aLat, aLng) : Infinity;
        const dB = bLat != null && bLng != null ? haversineDistanceMiles(userLocation.lat, userLocation.lng, bLat, bLng) : Infinity;
        return dA - dB;
      }
      return 0;
    });

  const isLoading = allProjectsQuery.isLoading;

  return (
    <div>
      {/* Page header */}
      <div style={{ marginBottom: "1.25rem" }}>
        <h1 style={{ fontSize: "1.875rem", fontWeight: 700, marginBottom: "0.25rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <HardHat style={{ width: "1.75rem", height: "1.75rem", color: "hsl(var(--primary))" }} />
          Field
        </h1>
        <p style={{ color: "hsl(var(--muted-foreground))", fontSize: "0.875rem" }}>
          Jobsite photos and time tracking for your active projects.
        </p>
      </div>

      {/* Tools banner */}
      <Link href="/field/tools">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.875rem",
            padding: "0.875rem 1rem",
            marginBottom: "1.25rem",
            backgroundColor: "hsl(var(--primary))",
            borderRadius: "0.5rem",
            cursor: "pointer",
            color: "hsl(var(--primary-foreground))",
          }}
          className="hover:opacity-90 transition-opacity"
        >
          <Wrench style={{ width: "1.25rem", height: "1.25rem", flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: "0.9375rem", lineHeight: 1.3 }}>Field Tools</div>
            <div style={{ fontSize: "0.8rem", opacity: 0.85, marginTop: "0.1rem" }}>Notes · calculators · wall framer · stairs · references</div>
          </div>
          <ChevronRight style={{ width: "1.125rem", height: "1.125rem", opacity: 0.7, flexShrink: 0 }} />
        </div>
      </Link>

      {/* Search + nearby */}
      <div style={{ display: "flex", gap: "0.75rem", marginBottom: "1rem", flexWrap: "wrap" }}>
        <div style={{ position: "relative", flex: 1, minWidth: "200px" }}>
          <Search style={{ position: "absolute", left: "0.75rem", top: "50%", transform: "translateY(-50%)", width: "1rem", height: "1rem", color: "hsl(var(--muted-foreground))" }} />
          <Input
            placeholder="Search projects…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ paddingLeft: "2.25rem" }}
          />
        </div>
        <Button
          variant={sortByNearest && userLocation ? "default" : "outline"}
          size="sm"
          onClick={handleNearbyToggle}
          disabled={isLocating}
          style={{ whiteSpace: "nowrap" }}
        >
          {isLocating ? (
            <Loader2 style={{ width: "0.875rem", height: "0.875rem", marginRight: "0.375rem" }} className="animate-spin" />
          ) : (
            <Navigation2 style={{ width: "0.875rem", height: "0.875rem", marginRight: "0.375rem" }} />
          )}
          {sortByNearest && userLocation ? "Nearest first" : "Nearby"}
        </Button>
      </div>

      {/* Project list */}
      {isLoading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "3rem" }}>
          <Loader2 style={{ width: "2rem", height: "2rem" }} className="animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: "3rem", color: "hsl(var(--muted-foreground))" }}>
          <HardHat style={{ width: "3rem", height: "3rem", margin: "0 auto 0.75rem", opacity: 0.3 }} />
          <p style={{ fontWeight: 500 }}>No active projects found</p>
          <p style={{ fontSize: "0.875rem", marginTop: "0.25rem" }}>Active projects will appear here.</p>
        </div>
      ) : (
        <div style={{ display: "grid", gap: "0.75rem" }}>
          {filtered.map((project: ProjectListItem) => {
            const pLat = (project as any).lat;
            const pLng = (project as any).lng;
            const dist =
              userLocation && pLat != null && pLng != null
                ? haversineDistanceMiles(userLocation.lat, userLocation.lng, pLat, pLng)
                : null;

            return (
              <Card
                key={project.id}
                style={{ cursor: "pointer", transition: "box-shadow 0.15s" }}
                className="hover:shadow-md"
                onClick={() => setLocation(`/field/${project.id}`)}
              >
                <CardContent style={{ padding: "1rem" }}>
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "0.75rem" }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                        <span style={{ fontWeight: 600, fontSize: "1rem" }}>{project.name}</span>
                        <Badge
                          style={{
                            backgroundColor: STATUS_COLORS[project.status] + "22",
                            color: STATUS_COLORS[project.status],
                            border: `1px solid ${STATUS_COLORS[project.status]}44`,
                            fontSize: "0.7rem",
                            padding: "0 0.4rem",
                          }}
                        >
                          {STATUS_LABELS[project.status] ?? project.status}
                        </Badge>
                      </div>
                      {(project as any).address && (
                        <div style={{ display: "flex", alignItems: "center", gap: "0.25rem", marginTop: "0.25rem", fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))" }}>
                          <MapPin style={{ width: "0.75rem", height: "0.75rem", flexShrink: 0 }} />
                          {(project as any).address}
                        </div>
                      )}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexShrink: 0 }}>
                      {dist !== null && (
                        <span style={{
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          padding: "0.125rem 0.5rem",
                          borderRadius: "9999px",
                          backgroundColor: dist < 1 ? "#d1fae5" : "hsl(var(--muted))",
                          color: dist < 1 ? "#065f46" : "hsl(var(--muted-foreground))",
                        }}>
                          {formatDist(dist)}
                        </span>
                      )}
                      <div style={{ display: "flex", alignItems: "center", gap: "0.25rem", fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))" }}>
                        <Camera style={{ width: "0.875rem", height: "0.875rem" }} />
                        <span>{(project as any).photoCount ?? 0}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
